import React, { useCallback, useEffect, useState } from 'react';
import { ClockIcon, CalendarIcon } from '@phosphor-icons/react';
import toast from 'react-hot-toast';

import { PageHeader } from '../../../components/PageHeader';
import { formatDate, formatInputDate, formatInputTime } from '../../../utils/date';
import { Input } from '../../../components/input/Input';
import { decimateAndFormatNumber } from '../../../utils/number';
import { calculateLinesTotalExclVat } from '../../order/utils/price';
import { SpinnerBlock } from '../../../components/Spinner';
import { TrailerTypeIcon } from '../../order/pages/order/TrailerTypes';
import { useTranslation } from '../../../contexts/translation-context';
import { PageHeading } from 'components/PageHeading';
import { Breadcrumb } from 'components/Breadcrumb';
import { Select } from 'components/select/Select';
import { Button } from 'components/button/Button';
import { OrderStatusTag } from '../../order/components/OrderStatusTag';
import { CarrierTermsCheckbox } from '../../order/components/CarrierTermsCheckbox';
import { OrderStatus, TrailerType } from 'src/types/enums';
import {
  getCarrierPortalOrders,
  getCarrierPortalTrucks,
  confirmCarrierPortalOrder,
  CarrierPortalOrder,
  CarrierPortalTruck,
} from '../models';
import { getDisplayError } from '../../../utils/get-display-error';
import { useCarrierPortalSupplier } from '../CarrierPortalCarrierContext';
import { trackEvent } from '../../../utils/track-event';
import { ErrorState } from '../../../components/error-state/ErrorState';
import { FilterField, FilterPanel } from 'components/page-templates/ListPage';

type OrderType = CarrierPortalOrder;
type TruckType = CarrierPortalTruck;

export interface IOrderProps {
  date: string;
  order: OrderType;
}

const Order: React.FC<IOrderProps> = (props) => {
  const { i18n } = useTranslation();

  const { order } = props;

  const linePurchases = order.lines.map((l) => l.purchases).flat();
  const purchaseTotal = calculateLinesTotalExclVat(linePurchases);

  return (
    <div className="card flex flex-col gap-3.5 p-5">
      <div className="flex items-center justify-between gap-4">
        <div className="heading-four">{order.orderNumber || i18n('carrierPortal.orders.draft')}</div>
        <div className="flex items-center gap-3">
          <div className="whitespace-nowrap text-sm">{`${i18n(
            'carrierPortal.orders.purchases',
          )} € ${decimateAndFormatNumber(purchaseTotal, 2, {
            decimalSeperator: ',',
          })}`}</div>
          <OrderStatusTag status={order.status as OrderStatus} />
        </div>
      </div>

      <div className="text-sm text-fg-secondary">
        {i18n('carrierPortal.orders.customerReference')}: {order.customerRef}
      </div>

      <div className="flex flex-col gap-3 border-y border-border py-3.5 text-sm">
        {order.lines.map((l) => {
          return (
            <div className="flex flex-col gap-1.5" key={l.id}>
              {l.stops
                .sort((a, b) => a.sequenceIndex - b.sequenceIndex)
                .map((s) => {
                  return (
                    <div className="grid grid-cols-5 gap-4" key={s.id}>
                      <div className="font-medium">
                        {s.type === 'Load' ? i18n('carrierPortal.orders.load') : i18n('carrierPortal.orders.unload')}
                      </div>
                      <div className="text-fg-secondary">{`${s.location.name} - ${s.location.country} ${s.location.city}`}</div>
                      <div className="text-fg-secondary">
                        {i18n('carrierPortal.orders.reference')}: {s.reference}
                      </div>
                      <div className="flex gap-1 items-center">
                        <CalendarIcon className="button-icon" /> {formatDate(s.date)}
                      </div>
                      <div className="flex gap-1 items-center">
                        <ClockIcon className="button-icon" />
                        {`${formatInputTime(s.timeStart)}-${formatInputTime(s.timeEnd)}`}
                      </div>
                    </div>
                  );
                })}
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-4">
        {order.allowedTrailerTypes.map((v) => {
          return <TrailerTypeIcon key={`${order.id}-${v}`} type={v as TrailerType} variant="small" />;
        })}
      </div>
    </div>
  );
};

export interface IPlanningContainerProps {
  date: string;
  truck: TruckType;
  orders: OrderType[];
}

const CarrierPlanningContainer: React.FC<IPlanningContainerProps> = (props) => {
  const { date, truck, orders } = props;
  const { i18n } = useTranslation();

  return (
    <div className="flex flex-col gap-4">
      <h2 className="heading-two">
        {i18n('carrierPortal.planning.ordersForTruck', { licensePlate: truck.licensePlate })}
      </h2>

      <div className="flex flex-col gap-4">
        {orders.map((v) => {
          return <Order key={v.id} date={date} order={v} />;
        })}
      </div>
    </div>
  );
};

export const CarrierPortalPlanningPage = () => {
  const { i18n, lang } = useTranslation();
  const { carrierId, supplier } = useCarrierPortalSupplier();
  const [date, setDate] = useState(() => formatInputDate(new Date()));
  const [trucks, setTrucks] = useState<TruckType[]>([]);
  const [selectedTruck, setSelectedTruck] = useState<TruckType | null>(null);
  const [orders, setOrders] = useState<OrderType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const termsRequired = supplier.carrierTermsRequired;
  const [loadError, setLoadError] = useState<unknown>(null);

  // Load trucks on mount
  useEffect(() => {
    getCarrierPortalTrucks(carrierId)
      .then((data) => {
        setLoadError(null);
        setTrucks(data);
        if (data.length > 0) {
          setSelectedTruck(data[0]);
        }
      })
      .catch((err) => {
        console.error('Failed to load trucks:', err);
        setLoadError(err);
        toast.error(getDisplayError(err));
      });
  }, [carrierId]);

  // Fetch orders when date or truck changes
  const fetchOrders = useCallback(async () => {
    if (!selectedTruck) {
      setOrders([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setLoadError(null);
    // Acceptance is per set of orders — a new date/truck (or a completed accept)
    // needs its own tick.
    setHasAcceptedTerms(false);
    try {
      const data = await getCarrierPortalOrders(carrierId, lang, {
        startDate: date,
        endDate: date,
        supplierTruckId: selectedTruck.id,
      });

      // Filter to only show relevant statuses
      const filteredOrders = data.filter(
        (v) => v.status === 'Created' || v.status === 'Placed' || v.status === 'Confirmed' || v.status === 'Executed',
      );
      setOrders(filteredOrders);
    } catch (err) {
      console.error('Failed to load orders:', err);
      setLoadError(err);
      toast.error(getDisplayError(err));
    } finally {
      setIsLoading(false);
    }
  }, [carrierId, lang, date, selectedTruck]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Gated on the terms checkbox above the button whenever acceptance is required:
  // accepting orders then always carries the carrier's explicit acceptance of the
  // general terms & conditions.
  const acceptAllOrders = async () => {
    setIsAccepting(true);
    try {
      let acceptedCount = 0;
      for (const order of orders) {
        if (order.status === 'Created' || order.status === 'Placed') {
          await confirmCarrierPortalOrder(carrierId, order.id);
          trackEvent('carrier_portal_order_accepted', {
            carrier_id: carrierId,
            order_id: order.id,
          });
          acceptedCount += 1;
        }
      }
      if (acceptedCount > 0) {
        trackEvent('carrier_portal_orders_bulk_accepted', {
          carrier_id: carrierId,
          accepted_count: acceptedCount,
        });
      }
      await fetchOrders();
      toast.success(i18n('carrierPortal.orders.allAccepted'));
    } catch (err) {
      toast.error(getDisplayError(err));
    } finally {
      setIsAccepting(false);
    }
  };

  return (
    <>
      <PageHeader title={i18n('carrierPortal.planning.planning')} />

      <PageHeading noMarginBottom leftSide={<Breadcrumb currentItem={i18n('carrierPortal.planning.planning')} />} />

      <div className="grid gap-4 p-7">
        <FilterPanel>
          <FilterField label={i18n('carrierPortal.planning.date')}>
            <Input type="date" value={date} onChange={setDate} />
          </FilterField>
          <FilterField label={i18n('carrierPortal.planning.truck')}>
            <Select
              items={trucks}
              keyName="licensePlate"
              selectedItem={selectedTruck}
              onSelect={setSelectedTruck}
              display={(r) => r.licensePlate}
            />
          </FilterField>
          {/* Plain div, not a FilterField: it wraps the terms checkbox, which has its own label. */}
          <div className="col-span-2 flex min-w-0 flex-col gap-1.5">
            <span className="label-text">{i18n('carrierPortal.planning.actions')}</span>
            <div className="flex flex-col gap-2">
              <CarrierTermsCheckbox
                isRequired={termsRequired}
                isChecked={hasAcceptedTerms}
                onChange={setHasAcceptedTerms}
                isDisabled={isAccepting}
              />
              <div>
                <Button
                  onTrigger={() => acceptAllOrders()}
                  isDisabled={isAccepting || (termsRequired && !hasAcceptedTerms)}
                  isLoading={isAccepting}
                >
                  {i18n('carrierPortal.planning.acceptAll')}
                </Button>
              </div>
            </div>
          </div>
        </FilterPanel>

        {loadError ? (
          <ErrorState
            error={loadError}
            onRetry={() => {
              // eslint-disable-next-line no-restricted-globals
              window.location.reload();
            }}
            variant="section"
          />
        ) : isLoading ? (
          <SpinnerBlock message={i18n('carrierPortal.planning.loadingOrders')} />
        ) : selectedTruck ? (
          <CarrierPlanningContainer date={date} key={`${formatDate(date)}`} orders={orders} truck={selectedTruck} />
        ) : (
          <div className="card p-5 text-sm text-fg-muted">{i18n('carrierPortal.planning.noTruck')}</div>
        )}
      </div>
    </>
  );
};
